import { ReactNode, useMemo } from 'react';
import Markdown from 'markdown-to-jsx';
import { BrandOptionFields } from '../../../models/course/ICourseContent';
import styles from './summary-page-template-old.module.scss';
import Link from 'next/link';
import { ISummaryPageSettings } from '../../../models/summary/ISummaryPageSettings';
import { ISummaryContent } from '../../../models/summary/ISummaryContent';
import { ISummaryData } from '../../../models/summary/ISummaryData';
import MediaGalleryComponentModel from '../../../models/components/media-gallery-component-model';
import MediaGallery from '../../molecules/media-gallery/media-gallery';
import { CourseType } from '../../../constants/course-type';
import { MediaAssetType } from '../../../constants/media-asset-type';
import { ApplicationBranding } from '../../../models/settings';
import { checkAdvertBannerContentExists } from '../../../utils/advert-banner/advert-banner-content';
import { ISummaryCourse } from '../../../models/summary/ISummaryCourse';
import { ApplicationType } from '../../../constants/application-type';
import { useTranslations } from 'next-intl';
import { getSummarySubMenu } from '../../../utils/jumplink-helpers/course-jumpLink';
import AdvertBanner from '../../molecules/advert-banner/advert-banner';
import SidebarNavigation from '../../molecules/sidebar-navigation/sidebar-navigation';
import SummaryDetailsOld from '../../molecules/summary-details-old/summary-details-old';

export type Props = {
  summaryPageSettings: ISummaryPageSettings;
  summaryContent: ISummaryContent;
  summaryData: ISummaryData[];
  children?: ReactNode;
  locales?: string[];
  pagePath?: string;
  application?: string;
  googleTagId?: string;
  courseList?: ISummaryCourse[];
};

export default function SummaryPageOld({
  summaryPageSettings,
  summaryContent,
  summaryData,
  pagePath,
  children,
  locales,
  application,
  googleTagId,
  courseList,
}: Props) {
  const translate = useTranslations();
  const summarySubMenu = useMemo(
    () => (application == ApplicationType.UniBrandedB2c ? getSummarySubMenu(translate) : null),
    [application, translate, pagePath],
  );
  // get Summary Page Title
  const getSummaryPageTitle = (): string => {
    let summaryPageTitle = '';

    summaryPageTitle = summaryContent?.summaryPageType?.fields?.programGroupName
      ? summaryContent?.summaryPageType?.fields?.name
      : summaryContent?.summaryPageType?.fields?.entryRoute
        ? summaryContent?.summaryPageType?.fields?.level +
          ` - ` +
          CourseType[
            Object.keys(CourseType).filter(
              (x) => x === summaryContent?.summaryPageType?.fields?.entryRoute,
            )[0]
          ]
        : null;

    return summaryPageTitle;
  };

  // customized brand options from CFv2
  // diff naming for duration etc.
  const brandOptions: BrandOptionFields = summaryContent?.brand?.fields?.brandOptions?.fields;

  // construct media gallery data
  let mediaGalleryComponent: any;
  if (summaryContent.mediaGallery?.fields?.gallery) {
    mediaGalleryComponent = new MediaGalleryComponentModel(
      summaryContent.mediaGallery?.fields,
      null,
    );
  }

  // check if advert banner data exist
  let checkAdvertExists = checkAdvertBannerContentExists(summaryContent.advertBanner?.fields);

  const summaryCTABranding: ApplicationBranding = {
    primaryColor: summaryPageSettings.backgroundColor,
    primaryHoverColor: summaryPageSettings.hoverBackgroundColor,
    primaryActiveColor: summaryPageSettings.activeBackgroundColor,
  };

  const summaryPageElements = (
    <section>
      {children && (
        <div className='summary-extra'>
          {/* Passing SEO tags from upper level as next/head component doesn't work from here */}
          {children}
        </div>
      )}
      <div className={`${styles[`summary-info`]} mb-md-4 mb-3`}>
        <div className='row'>
          <div className={brandOptions?.navigation ? 'col' : 'col-md-9'}>
            {!!summaryContent && (
              <h2 className={styles[`summary-name`]}>{getSummaryPageTitle()}</h2>
            )}
            <h5 className={styles[`university-name`]}>
              <Link href={summaryPageSettings.brandURL}>
                {summaryContent?.brand?.fields.brandName}
              </Link>
            </h5>
            {!!summaryContent.shortDescription?.length && (
              <div id='summary-overview' className={styles[`summary-short-desc-block`]}>
                <div className='row'>
                  <div className='col-md-12'>
                    <section className={styles[`summary-short-desc`]}>
                      <Markdown>{summaryContent.shortDescription}</Markdown>
                    </section>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`${styles[`summary-key-information-block`]} mb-md-4 mb-3`}>
        {!!summaryContent?.keyInformation && (
          <>
            <h3 id='key-information' className={styles[`summary-sub-heading`]}>
              Key Information
            </h3>
            <section className={styles[`summary-key-information`]}>
              <Markdown>{summaryContent?.keyInformation}</Markdown>
            </section>
          </>
        )}
      </div>
      {mediaGalleryComponent != null &&
      (summaryContent.showMediaAsset === MediaAssetType.MediaGallery ||
        summaryContent.showMediaAsset === MediaAssetType.Both) ? (
        <div className='row'>
          <div className='col-md-12 gallery'>
            <MediaGallery galleryItems={mediaGalleryComponent?.gallery} />
          </div>
        </div>
      ) : (
        <></>
      )}
      {checkAdvertExists &&
      (summaryContent.showMediaAsset === MediaAssetType.AdvertBanner ||
        summaryContent.showMediaAsset === MediaAssetType.Both) ? (
        <div className='mb-md-4 mb-3'>
          <AdvertBanner
            advertBanner={summaryContent.advertBanner.fields}
            appBranding={summaryCTABranding}
            application={application}
          />
        </div>
      ) : (
        <></>
      )}
      <div className={`${styles[`summary-details-block`]} mb-md-4 mb-3`}>
        {summaryData != null && summaryData?.length > 0 ? (
          <SummaryDetailsOld
            summaryPageSettings={summaryPageSettings}
            summaryData={summaryData}
            summaryContent={summaryContent}
            googleTagId={googleTagId}
            courseList={courseList}
          />
        ) : (
          <></>
        )}
      </div>
    </section>
  );

  return (
    <>
      {brandOptions?.navigation || application == ApplicationType.UniBrandedB2c ? (
        <div className='custom-page-template'>
          <div className='row'>
            <div className='col-lg-3 px-0 px-lg-3'>
              <SidebarNavigation
                sidebarNavigationProps={
                  application == ApplicationType.UniBrandedB2c
                    ? summarySubMenu
                    : brandOptions?.navigation?.fields
                }
                sidebarBrandColor={summaryPageSettings.backgroundColor}
                b2cFlag={summaryPageSettings.b2cFlag}
                pagePath={pagePath}
                locales={locales}
              />
            </div>
            <div className='col-lg-9 py-lg-4 py-0'>{summaryPageElements}</div>
          </div>
        </div>
      ) : (
        <>{summaryPageElements}</>
      )}
    </>
  );
}
